import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { formatToDateTimeString } from "../helpers/dbDateFormat";
import { setHours, setMinutes, isSameDay, addDays } from "date-fns";
import { enIN } from "date-fns/locale/en-IN";
import * as XLSX from "xlsx";
import Multiselect from "multiselect-react-dropdown";
import GetUserLocationList from "../hooks/getUserLocationList";
import getMobileNumbersByCity from "../hooks/getMobileNumbersByCity";

// ─── Style tokens (same as event-creations-features-questions) ───────────────
const sectionCardCls =
  "space-y-4 rounded-2xl border border-slate-200 bg-white/95 p-5 shadow-sm shadow-slate-200/60";

const optionCardCls = (active) =>
  `flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 transition-all ${
    active
      ? "border-teal-500 bg-teal-50 text-slate-900 shadow-sm shadow-teal-100"
      : "border-slate-200 bg-slate-50/70 text-slate-700 hover:border-teal-300 hover:bg-teal-50/60"
  }`;

const fieldCls =
  "w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-800 outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-500/15 no-translate";

const fileCls =
  "w-full cursor-pointer rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition hover:border-teal-400 hover:bg-teal-50/40 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/15";

const errorCls =
  "flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700";

// ─── Component ────────────────────────────────────────────────────────────────
const EventAddServiceModal = ({ eventDetails, setIsOpen, confirmSubmission }) => {
  // ─── ALL HOOKS MUST BE CALLED UNCONDITIONALLY BEFORE EARLY RETURN ───────
  const [userLocationList, setUserLocationList] = useState([]);
  const [audioDuration, setAudioDuration] = useState(null);
  const [audioCredits, setAudioCredits] = useState(0);
  const [matchedMobileCount, setMatchedMobileCount] = useState(0);
  const [isCheckingLocations, setIsCheckingLocations] = useState(false);
  const [locationMatchError, setLocationMatchError] = useState("");

  useEffect(() => {
    (async () => {
      const locationList = await GetUserLocationList();
      if (!locationList) return;
      setUserLocationList(locationList.map((item) => ({ name: item, id: item })));
    })();
  }, []);

  // ─── Derived values with safe defaults (eventDetails may be null at this point) ──
  const ev = eventDetails || {};
  const isVoiceEnabled = Number(ev.voice) === 1;
  const isSmsEnabled = Number(ev.sms) === 1;
  const isConfirmSmsEnabled = Number(ev.confirmSms) === 1;
  const hasReminderOptions = !isVoiceEnabled || !isSmsEnabled;

  // ─── Validation schema (must be unconditionally defined before useFormik) ───────
  const validationSchema = Yup.object({
    ...(hasReminderOptions
      ? {
          reminderService: Yup.array()
            .of(Yup.string())
            .test(
              "no-exclusive",
              "You cannot select other options if 'No Reminders' is selected.",
              (value) => !(value.includes("no") && value.length > 1),
            )
            .min(1, "Please select at least one option for reminder service.")
            .required("Please select at least one option for reminder service."),
        }
      : {}),
    ...(!isVoiceEnabled
      ? {
          voiceCallSchedule: Yup.date()
            .nullable()
            .when("reminderService", {
              is: (val) => Array.isArray(val) && val.includes("voiceCall"),
              then: (schema) =>
                schema
                  .min(new Date(), "Please select a future date and time")
                  .required("Voice Call schedule is required."),
            }),
          voiceCallFile: Yup.mixed()
            .nullable()
            .when("reminderService", {
              is: (val) => Array.isArray(val) && val.includes("voiceCall"),
              then: (schema) =>
                schema
                  .required("Please upload a WAV file for the voice call.")
                  .test(
                    "file-format",
                    "Only .wav files are allowed.",
                    (value) =>
                      value &&
                      value.name &&
                      value.name.toLowerCase().endsWith(".wav"),
                  )
                  .test(
                    "file-duration",
                    "Voice media should be less than or equal to 60 seconds (1 minute).",
                    (value) =>
                      new Promise((resolve) => {
                        if (!value) return resolve(true);
                        const audio = document.createElement("audio");
                        audio.src = URL.createObjectURL(value);
                        audio.addEventListener("loadedmetadata", () => {
                          resolve(audio.duration <= 60);
                          URL.revokeObjectURL(audio.src);
                        });
                        audio.addEventListener("error", () => {
                          URL.revokeObjectURL(audio.src);
                          resolve(false);
                        });
                      }),
                  ),
            }),
        }
      : {}),
    ...(!isSmsEnabled
      ? {
          smsSchedule: Yup.date()
            .nullable()
            .when("reminderService", {
              is: (val) => Array.isArray(val) && val.includes("sms"),
              then: (schema) =>
                schema
                  .min(new Date(), "Please select a future date and time")
                  .required("SMS schedule is required."),
            }),
        }
      : {}),
    ...(!isConfirmSmsEnabled
      ? {
          moiConfirmation: Yup.string().required("Please select an option."),
        }
      : {}),
    hasCustomList: Yup.boolean(),
    customListFile: Yup.mixed()
      .nullable()
      .when("hasCustomList", {
        is: true,
        then: (schema) =>
          schema
            .test(
              "custom-list-required",
              "Please upload an Excel/CSV file OR select at least one location.",
              function (value) {
                const { selectedLocations } = this.parent;
                return Boolean(
                  value || (selectedLocations && selectedLocations.length > 0),
                );
              },
            )
            .test(
              "file-format",
              "Only .xls, .xlsx, or .csv files are allowed.",
              (value) =>
                !value ||
                value.name.toLowerCase().endsWith(".xls") ||
                value.name.toLowerCase().endsWith(".xlsx") ||
                value.name.toLowerCase().endsWith(".csv"),
            ),
        otherwise: (schema) => schema.nullable(),
      }),
  });

  const formik = useFormik({
    initialValues: {
      reminderService: [],
      smsSchedule: null,
      voiceCallSchedule: null,
      voiceCallFile: null,
      voiceCallCredits: 0,
      moiConfirmation: "",
      hasCustomList: false,
      customListFile: null,
      selectedLocations: [],
    },
    validationSchema,
    onSubmit: async (values, { resetForm, setFieldError }) => {
      if (
        values.hasCustomList &&
        values.selectedLocations.length > 0 &&
        !values.customListFile &&
        matchedMobileCount === 0
      ) {
        setLocationMatchError(
          "Select a location with at least one matched mobile number or upload an Excel/CSV file.",
        );
        return;
      }

      // Manual voice file validation
      if (
        Array.isArray(values.reminderService) &&
        values.reminderService.includes("voiceCall") &&
        values.voiceCallFile
      ) {
        const file = values.voiceCallFile;
        if (!file.name.toLowerCase().endsWith(".wav")) {
          setFieldError("voiceCallFile", "Only .wav files are allowed.");
          return;
        }

        const duration = await new Promise((resolve) => {
          const audio = document.createElement("audio");
          audio.src = URL.createObjectURL(file);
          audio.addEventListener("loadedmetadata", () => {
            resolve(audio.duration);
            URL.revokeObjectURL(audio.src);
          });
          audio.addEventListener("error", () => {
            resolve(null);
            URL.revokeObjectURL(audio.src);
          });
        });

        if (duration === null) {
          setFieldError("voiceCallFile", "Unable to read audio file. Please try again.");
          return;
        }
        if (duration > 60) {
          setFieldError(
            "voiceCallFile",
            "Voice media should be less than or equal to 60 seconds (1 minute).",
          );
          return;
        }

        let credits = 0;
        if (duration <= 20) credits = 1;
        else if (duration <= 40) credits = 2;
        else if (duration <= 60) credits = 3;
        values.voiceCallCredits = credits;
      }

      const alteredValue = {
        reminder_sms: Array.isArray(values.reminderService) && values.reminderService.includes("sms") ? 1 : 0,
        reminder_voice: Array.isArray(values.reminderService) && values.reminderService.includes("voiceCall") ? 1 : 0,
        reminder_sms_date:
          Array.isArray(values.reminderService) &&
          values.reminderService.includes("sms") &&
          values.smsSchedule
            ? formatToDateTimeString(values.smsSchedule)
            : null,
        reminder_call_date:
          Array.isArray(values.reminderService) &&
          values.reminderService.includes("voiceCall") &&
          values.voiceCallSchedule
            ? formatToDateTimeString(values.voiceCallSchedule)
            : null,
        reminder_voice_id: values.voiceCallFile,
        voice_call_credits: values.voiceCallCredits || 0,
        moi_confirmation_sms: values.moiConfirmation !== "no" && values.moiConfirmation !== "" ? 1 : 0,
        has_custom_list: values.hasCustomList ? 1 : 0,
        custom_list_file: values.customListFile,
        custom_list_locations: values.selectedLocations.map((item) => item.id),
      };

      confirmSubmission(alteredValue, resetForm);
    },
  });

  // ─── Location count effect ─────────────────────────────────────────────────
  useEffect(() => {
    if (!formik.values.hasCustomList || formik.values.selectedLocations.length === 0) {
      setMatchedMobileCount(0);
      setLocationMatchError("");
      setIsCheckingLocations(false);
      return;
    }

    let isActive = true;
    const fetchMobileCount = async () => {
      setIsCheckingLocations(true);
      setLocationMatchError("");
      const ids = formik.values.selectedLocations.map((item) => item.id);
      const mobileNumbers = await getMobileNumbersByCity(ids, { showToast: false });
      if (!isActive) return;
      if (Array.isArray(mobileNumbers)) {
        setMatchedMobileCount(mobileNumbers.length);
        if (mobileNumbers.length === 0) {
          setLocationMatchError("No mobile numbers were found for the selected locations.");
        }
      } else {
        setMatchedMobileCount(0);
        setLocationMatchError("We could not verify the mobile number count for the selected locations.");
      }
      setIsCheckingLocations(false);
    };

    fetchMobileCount();
    return () => { isActive = false; };
  }, [formik.values.hasCustomList, formik.values.selectedLocations]);

  // ─── Date helpers (same logic as QuestionsPopup) ───────────────────────────
  const now = new Date();
  const getMinDate = () => (now.getHours() >= 18 ? addDays(now, 1) : now);

  const getVoiceCallMinTime = () => {
    if (formik.values.voiceCallSchedule && isSameDay(formik.values.voiceCallSchedule, now)) {
      return now;
    }
    // Use the selected date as the base, defaulting to today if not set
    const baseDate = formik.values.voiceCallSchedule || new Date();
    return setHours(setMinutes(new Date(baseDate), 0), 9);
  };

  const getVoiceCallMaxTime = () => {
    const baseDate = formik.values.voiceCallSchedule || new Date();
    return setHours(setMinutes(new Date(baseDate), 0), 18);
  };

  const getSmsMinTime = () => {
    if (formik.values.smsSchedule && isSameDay(formik.values.smsSchedule, now)) {
      return now;
    }
    // Use the selected date as the base, defaulting to today if not set
    const baseDate = formik.values.smsSchedule || new Date();
    return setHours(setMinutes(new Date(baseDate), 0), 9);
  };

  const getSmsMaxTime = () => {
    const baseDate = formik.values.smsSchedule || new Date();
    return setHours(setMinutes(new Date(baseDate), 0), 18);
  };

  // ─── Credit calculation ────────────────────────────────────────────────────
  const calculateCredits = (duration) => {
    if (duration <= 20) return 1;
    if (duration <= 40) return 2;
    if (duration <= 60) return 3;
    return 0;
  };

  // ─── File handler ──────────────────────────────────────────────────────────
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    formik.setFieldValue("voiceCallFile", file);

    if (file) {
      const duration = await new Promise((resolve) => {
        const audio = document.createElement("audio");
        audio.src = URL.createObjectURL(file);
        audio.addEventListener("loadedmetadata", () => {
          resolve(audio.duration);
          URL.revokeObjectURL(audio.src);
        });
        audio.addEventListener("error", () => {
          resolve(null);
          URL.revokeObjectURL(audio.src);
        });
      });

      if (duration !== null) {
        setAudioDuration(duration);
        setAudioCredits(calculateCredits(duration));
      } else {
        setAudioDuration(null);
        setAudioCredits(0);
      }
    } else {
      setAudioDuration(null);
      setAudioCredits(0);
    }

    formik.setFieldTouched("voiceCallFile", true, false);
  };

  // ─── Reminder service checkbox handler ────────────────────────────────────
  const handleReminderServiceChange = (event) => {
    const { value, checked } = event.target;
    let updated = [...formik.values.reminderService];

    if (checked) {
      if (value === "no") {
        updated = ["no"];
        formik.setFieldValue("smsSchedule", null);
        formik.setFieldValue("voiceCallSchedule", null);
        formik.setFieldValue("voiceCallFile", null);
        formik.setFieldValue("hasCustomList", false);
        formik.setFieldValue("customListFile", null);
        formik.setFieldValue("selectedLocations", []);
        setAudioDuration(null);
        setAudioCredits(0);
      } else {
        updated = updated.filter((item) => item !== "no");
        updated.push(value);
      }
    } else {
      updated = updated.filter((item) => item !== value);
    }

    formik.setFieldValue("reminderService", updated);

    if (value === "sms" && !checked) formik.setFieldValue("smsSchedule", null);
    if (value === "voiceCall" && !checked) {
      formik.setFieldValue("voiceCallSchedule", null);
      formik.setFieldValue("voiceCallFile", null);
      setAudioDuration(null);
      setAudioCredits(0);
    }
  };

  // ─── Excel template download ───────────────────────────────────────────────
  const downloadExcelTemplate = () => {
    const emptyData = Array.from({ length: 10 }, () => ({
      "Mobile Number": "",
      Name: "",
    }));
    const workbook = XLSX.utils.book_new();
    const sheet = XLSX.utils.json_to_sheet(emptyData);
    sheet["!cols"] = [{ wch: 15 }, { wch: 20 }];
    XLSX.utils.book_append_sheet(workbook, sheet, "Template");
    XLSX.writeFile(workbook, "custom-list-template.xlsx");
  };

  const showVoiceCallPicker =
    Array.isArray(formik.values.reminderService) &&
    formik.values.reminderService.includes("voiceCall");
  const showSmsPicker =
    Array.isArray(formik.values.reminderService) &&
    formik.values.reminderService.includes("sms");
  const showCustomList = showVoiceCallPicker || showSmsPicker;

  // Early return AFTER all hooks - don't render if no event details
  if (!eventDetails) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-3xl border border-white/20 bg-[linear-gradient(180deg,#f8fbfd_0%,#eef4f8_100%)] shadow-2xl shadow-slate-900/20">

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-white/10 bg-[linear-gradient(135deg,#0f172a_0%,#16324b_48%,#0f766e_100%)] px-8 py-6 text-white">
          <div>
            <h2 className="text-2xl font-black tracking-tight">Add More Services</h2>
            <p className="mt-1 text-sm text-slate-200/85">
              Configure additional reminder services for this event
            </p>
          </div>
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/10 text-white/80 transition hover:bg-white/20 hover:text-white"
          >
            ✕
          </button>
        </div>

        <form onSubmit={formik.handleSubmit} className="space-y-6 p-8">

          {/* ── Reminder Service Section ───────────────────────────────────── */}
          {hasReminderOptions && (
            <div className={sectionCardCls}>
              <label className="block text-base font-bold text-slate-900">
                <span className="flex items-center gap-2">📣 Reminder Service</span>
                <p className="mt-1 text-sm font-normal text-slate-500">
                  Select the reminder services to add to this event.
                </p>
              </label>
              <div className="grid grid-cols-2 gap-4 md:flex md:flex-wrap">
                {!isVoiceEnabled && (
                  <label className={optionCardCls(formik.values.reminderService.includes("voiceCall"))}>
                    <input
                      type="checkbox"
                      value="voiceCall"
                      onChange={handleReminderServiceChange}
                      checked={formik.values.reminderService.includes("voiceCall")}
                      className="h-5 w-5 cursor-pointer accent-teal-600"
                    />
                    <span className="font-semibold">Voice Call</span>
                  </label>
                )}
                {!isSmsEnabled && (
                  <label className={optionCardCls(formik.values.reminderService.includes("sms"))}>
                    <input
                      type="checkbox"
                      value="sms"
                      onChange={handleReminderServiceChange}
                      checked={formik.values.reminderService.includes("sms")}
                      className="h-5 w-5 cursor-pointer accent-teal-600"
                    />
                    <span className="font-semibold">SMS</span>
                  </label>
                )}
                <label className={optionCardCls(formik.values.reminderService.includes("no"))}>
                  <input
                    type="checkbox"
                    value="no"
                    onChange={handleReminderServiceChange}
                    checked={formik.values.reminderService.includes("no")}
                    className="h-5 w-5 cursor-pointer accent-teal-600"
                  />
                  <span className="font-semibold">No Reminders</span>
                </label>
              </div>
              {formik.touched.reminderService && formik.errors.reminderService && (
                <div className={errorCls}>
                  <span className="font-semibold text-red-600">⚠</span>
                  <p className="text-sm text-red-700">{formik.errors.reminderService}</p>
                </div>
              )}
            </div>
          )}

          {/* ── Voice Call Configuration ───────────────────────────────────── */}
          {showVoiceCallPicker && (
            <div className={sectionCardCls}>
              <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900">
                📞 Voice Call Configuration
              </h3>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">
                  Schedule Voice Call
                </label>
                <DatePicker
                  selected={formik.values.voiceCallSchedule}
                  onChange={(date) => formik.setFieldValue("voiceCallSchedule", date)}
                  showTimeSelect
                  locale={enIN}
                  dateFormat="dd/MM/yyyy h:mm aa"
                  className={fieldCls}
                  placeholderText="Select date and time"
                  minDate={getMinDate()}
                  minTime={getVoiceCallMinTime()}
                  maxTime={getVoiceCallMaxTime()}
                />
                <p className="text-xs text-slate-500">Available 9 AM – 6 PM</p>
                {formik.touched.voiceCallSchedule && formik.errors.voiceCallSchedule && (
                  <div className={errorCls}>
                    <span className="text-red-600">⚠</span>
                    <p className="text-sm text-red-700">{formik.errors.voiceCallSchedule}</p>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">
                  🎶 Upload Voice File (WAV format)
                </label>
                <div className="relative">
                  <input
                    type="file"
                    accept=".wav"
                    onChange={handleFileChange}
                    className={fileCls}
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Only WAV format files are allowed. Maximum duration: 60 seconds. Credits based on duration:
                  </p>
                  <ul className="list-inside list-disc text-xs text-slate-500">
                    <li>Up to 20 seconds: 1 credit</li>
                    <li>20–40 seconds: 2 credits</li>
                    <li>40–60 seconds: 3 credits</li>
                  </ul>
                </div>

                {formik.values.voiceCallFile && (
                  <div className="mt-2 rounded-xl border border-teal-200 bg-teal-50/80 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">
                          File: {formik.values.voiceCallFile.name}
                        </p>
                        {audioDuration !== null && (
                          <>
                            <p className="text-sm text-slate-600">
                              Duration: {audioDuration.toFixed(1)} seconds
                            </p>
                            <p className="text-sm text-slate-600">
                              Credits Required: {audioCredits}
                            </p>
                          </>
                        )}
                        {audioDuration === null && (
                          <p className="text-sm text-amber-600">Analyzing audio file...</p>
                        )}
                      </div>
                      <div className="text-right">
                        {audioDuration !== null && (
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                              audioCredits === 1
                                ? "bg-emerald-100 text-emerald-700"
                                : audioCredits === 2
                                  ? "bg-amber-100 text-amber-700"
                                  : "bg-rose-100 text-rose-700"
                            }`}
                          >
                            {audioCredits} Credit{audioCredits > 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {formik.touched.voiceCallFile && formik.errors.voiceCallFile && (
                  <div className={errorCls}>
                    <span className="text-red-600">⚠</span>
                    <p className="text-sm text-red-700">{formik.errors.voiceCallFile}</p>
                  </div>
                )}
                <a
                  href="https://voicemaker.in/"
                  className="mt-2 flex items-center gap-1 text-sm font-semibold text-teal-700 transition-colors hover:text-teal-800"
                  rel="noreferrer"
                  target="_blank"
                >
                  🔗 Create AI Voice
                </a>
              </div>
            </div>
          )}

          {/* ── SMS Configuration ─────────────────────────────────────────── */}
          {showSmsPicker && (
            <div className={sectionCardCls}>
              <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900">
                📧 SMS Reminder Configuration
              </h3>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">
                  Schedule SMS Reminder
                </label>
                <DatePicker
                  selected={formik.values.smsSchedule}
                  onChange={(date) => formik.setFieldValue("smsSchedule", date)}
                  showTimeSelect
                  locale={enIN}
                  dateFormat="dd/MM/yyyy h:mm aa"
                  className={fieldCls}
                  placeholderText="Select date and time"
                  minDate={getMinDate()}
                  minTime={getSmsMinTime()}
                  maxTime={getSmsMaxTime()}
                />
                <p className="text-xs text-slate-500">Available 9 AM – 6 PM</p>
                {formik.touched.smsSchedule && formik.errors.smsSchedule && (
                  <div className={errorCls}>
                    <span className="text-red-600">⚠</span>
                    <p className="text-sm text-red-700">{formik.errors.smsSchedule}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Custom Contact List ───────────────────────────────────────── */}
          {showCustomList && (
            <div className={sectionCardCls}>
              <label className="flex cursor-pointer items-center gap-3">
                <input
                  type="checkbox"
                  name="hasCustomList"
                  checked={formik.values.hasCustomList}
                  onChange={(e) => {
                    formik.setFieldValue("hasCustomList", e.target.checked);
                    if (!e.target.checked) {
                      formik.setFieldValue("customListFile", null);
                      formik.setFieldValue("selectedLocations", []);
                      setMatchedMobileCount(0);
                      setLocationMatchError("");
                    }
                  }}
                  className="h-5 w-5 cursor-pointer accent-teal-600"
                />
                <span className="text-base font-bold text-slate-900">
                  📍 Use Custom List for Reminders?
                </span>
              </label>

              {formik.touched.customListFile && formik.errors.customListFile && (
                <div className={errorCls}>
                  <span className="font-semibold text-red-600">⚠</span>
                  <p className="text-sm text-red-700">{formik.errors.customListFile}</p>
                </div>
              )}

              {formik.values.hasCustomList && (
                <div className="mt-4 space-y-6">
                  {/* Upload Excel */}
                  <div className="space-y-2">
                    <p className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-teal-600 text-xs text-white">1</span>
                      Upload Excel List
                    </p>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={downloadExcelTemplate}
                        className="flex items-center gap-2 rounded-xl bg-[linear-gradient(135deg,#0f766e_0%,#155e75_100%)] px-4 py-2 text-sm font-semibold text-white shadow-sm shadow-teal-200 transition hover:opacity-90"
                      >
                        📥 Download Template
                      </button>
                      <p className="flex items-center text-xs text-slate-500">
                        Download an Excel template with example columns
                      </p>
                    </div>
                    <input
                      type="file"
                      accept=".xls,.xlsx,.csv"
                      onChange={(e) => {
                        formik.setFieldValue("customListFile", e.target.files[0]);
                        formik.setFieldTouched("customListFile", true, false);
                      }}
                      className={fileCls}
                    />
                    <p className="text-xs text-slate-500">Supported formats: .xls, .xlsx, .csv</p>
                    {formik.values.customListFile && (
                      <p className="text-xs font-semibold text-emerald-600">
                        ✓ File selected: {formik.values.customListFile.name}
                      </p>
                    )}
                  </div>

                  {/* OR divider */}
                  <div className="flex items-center gap-3">
                    <div className="flex-1 border-t border-slate-300" />
                    <span className="px-2 text-sm font-semibold text-slate-500">OR</span>
                    <div className="flex-1 border-t border-slate-300" />
                  </div>

                  {/* Select Locations */}
                  <div className="space-y-2">
                    <p className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-teal-600 text-xs text-white">2</span>
                      Select Cities/Locations
                    </p>
                    <Multiselect
                      options={userLocationList}
                      selectedValues={formik.values.selectedLocations}
                      onSelect={(list) => formik.setFieldValue("selectedLocations", list)}
                      onRemove={(list) => formik.setFieldValue("selectedLocations", list)}
                      displayValue="name"
                      placeholder="Search and select locations"
                      style={{
                        chips: { background: "#0f766e" },
                        multiselectContainer: { color: "#334155" },
                        searchBox: {
                          border: "1px solid #cbd5e1",
                          borderRadius: "0.75rem",
                          padding: "0.75rem",
                          background: "#f8fafc",
                        },
                      }}
                    />

                    {formik.values.selectedLocations.length > 0 && (
                      <div className="mt-3 space-y-3 rounded-xl border border-teal-200 bg-teal-50/70 p-4">
                        <p className="text-sm font-semibold text-slate-800">
                          Selected:{" "}
                          {formik.values.selectedLocations.map((l) => l.name).join(", ")}
                        </p>
                        <div className="rounded-xl bg-white px-4 py-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Matched Mobile Numbers
                          </p>
                          <p className="mt-1 text-2xl font-black text-slate-900">
                            {isCheckingLocations ? "Checking..." : matchedMobileCount}
                          </p>
                        </div>
                        {!isCheckingLocations && matchedMobileCount > 0 && (
                          <p className="text-sm text-emerald-700">
                            These matched mobile numbers will be added to the event automation list.
                          </p>
                        )}
                        {locationMatchError && (
                          <p className="text-sm text-red-700">{locationMatchError}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── MOI Confirmation ──────────────────────────────────────────── */}
          {!isConfirmSmsEnabled && (
            <div className={sectionCardCls}>
              <label className="block text-base font-bold text-slate-900">
                <span className="flex items-center gap-2">💬 Moi Confirmation Message</span>
                <p className="mt-1 text-sm font-normal text-slate-500">
                  Send a confirmation message when MOI is received?
                </p>
              </label>
              <div className="grid grid-cols-2 gap-3">
                {["yes", "no"].map((value) => (
                  <label
                    key={value}
                    className={optionCardCls(formik.values.moiConfirmation === value)}
                  >
                    <input
                      type="radio"
                      name="moiConfirmation"
                      value={value}
                      onChange={formik.handleChange}
                      checked={formik.values.moiConfirmation === value}
                      className="h-5 w-5 cursor-pointer accent-teal-600"
                    />
                    <span className="font-semibold">
                      {value === "yes" ? "✓ Yes" : "✗ No"}
                    </span>
                  </label>
                ))}
              </div>
              {formik.touched.moiConfirmation && formik.errors.moiConfirmation && (
                <div className={errorCls}>
                  <span className="text-red-600">⚠</span>
                  <p className="text-sm text-red-700">{formik.errors.moiConfirmation}</p>
                </div>
              )}
            </div>
          )}

          {/* ── Action Buttons ────────────────────────────────────────────── */}
          <div className="flex gap-3 border-t border-slate-200 pt-2">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={formik.isSubmitting}
              className="flex-1 rounded-xl bg-[linear-gradient(135deg,#0f766e_0%,#155e75_100%)] px-6 py-3 font-bold text-white shadow-lg shadow-teal-200 transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {formik.isSubmitting ? "Adding Services..." : "✓ Add Services to Event"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventAddServiceModal;
